import {
  parseBodyMapFromData,
  parseContentMapFromData,
  parseMaterialMapFromData,
  readActivityDataField,
  checkAnswerState,
} from "../../utilization/CatchtivityUtilization";
import { shuffleArray } from "../../utilization/AppUtilization";
import i18n from "../../language/i18n";
import ActivityBodyContent from "./body-contents/ActivityBodyContent";
import InlineSentenceBody, {
  renderInlineParts,
} from "./body-contents/InlineSentenceBody";
import FillInTheBlanksActivityMaterialContent from "./material-contents/FillInTheBlanksActivityMaterialContent";
import { IFillInTheBlanksActivityProps } from "../../properties/ActivityProperties";
import ActivitySolveLayout from "./ActivitySolveLayout";
import { useState, useEffect, useRef } from "react";

const FillInTheBlanksActivityContent = ({
  answerMap,
  data,
  canAnswerQuestion,
  changeAnswer,
  isPreview,
  showCorrectAnswer,
  isFullScreen,
  passage,
}: IFillInTheBlanksActivityProps) => {
  const contentMap = parseContentMapFromData(data);
  const fillInTheBlanksBodyMap = parseBodyMapFromData(data, "FILL_IN_THE_BLANKS");
  const fillInTheBlanksMaterialMap = parseMaterialMapFromData(
    data,
    "FILL_IN_THE_BLANKS",
  );
  const incorrectListRaw = readActivityDataField(
    data,
    "FILL_IN_THE_BLANKS",
    "incorrectList",
  );
  const fillInTheBlanksIncorrectList = incorrectListRaw
    ? JSON.parse(incorrectListRaw)
    : [];

  const [currentAnswerMap, setCurrentAnswerMap] = useState(answerMap);
  // V4 target model: the highlighted blank the next word-bank tap will fill
  // (null → first empty blank).
  const [fillTarget, setFillTarget] = useState<number | null>(null);

  useEffect(() => {
    setCurrentAnswerMap(answerMap);
    setFillTarget(null);
  }, [answerMap]);

  const constructAnswerOptionList = () => {
    const optionList: any = [];
    Object.keys(fillInTheBlanksMaterialMap).forEach((key) => {
      const array = JSON.parse(fillInTheBlanksMaterialMap[key]);
      for (const item of array) {
        if (optionList.findIndex((option: string) => option === item) === -1) {
          optionList.push(item);
        }
      }
    });
    fillInTheBlanksIncorrectList.forEach((value: string) => {
      optionList.push(value);
    });
    return optionList;
  };

  // Pool order: shuffled once PER ACTIVITY (content-keyed — the component
  // instance survives across activities of the same type, so per-mount state
  // would show the previous activity's word bank).
  const poolKey = JSON.stringify([fillInTheBlanksMaterialMap, fillInTheBlanksIncorrectList]);
  const [shuffledOptionList, setShuffledOptionList] = useState<any[]>(() =>
    shuffleArray([...constructAnswerOptionList()]),
  );
  const poolKeyRef = useRef<string>(poolKey);
  useEffect(() => {
    if (poolKeyRef.current === poolKey) return;
    poolKeyRef.current = poolKey;
    setShuffledOptionList(shuffleArray([...constructAnswerOptionList()]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey]);

  const handleFillInTheBlanksAnswerOnChange = (
    _answerMap: any,
    key: any,
    value: string | null,
  ) => {
    if (isPreview) return;
    const next = { ...currentAnswerMap, [key]: value };
    setCurrentAnswerMap(next);
    changeAnswer(next);
  };

  // Media options keep the original drag/drop layout (user rule: media → different design).
  if (contentMap.type !== "TEXT") {
    return (
      <ActivitySolveLayout
        variant="split"
        isFullScreen={isFullScreen}
        passage={passage}
        body={
          <ActivityBodyContent
            bodyMap={fillInTheBlanksBodyMap}
            answerMap={
              showCorrectAnswer
                ? Object.keys(fillInTheBlanksMaterialMap).reduce(
                    (acc: any, key: string) => {
                      acc[key] = JSON.parse(fillInTheBlanksMaterialMap[key])[0];
                      return acc;
                    },
                    {},
                  )
                : currentAnswerMap
            }
            contentMap={contentMap}
            templateType={"FILL_IN_THE_BLANKS"}
          />
        }
        interaction={
          <FillInTheBlanksActivityMaterialContent
            uniqueValue={JSON.stringify(data.contentMap)}
            answerMap={answerMap}
            optionList={constructAnswerOptionList()}
            materialMap={fillInTheBlanksMaterialMap}
            contentMap={contentMap}
            checkCanAnswerQuestion={canAnswerQuestion}
            onChange={handleFillInTheBlanksAnswerOnChange}
            isPreview={isPreview}
            showCorrectAnswer={showCorrectAnswer}
          />
        }
      />
    );
  }

  const correctFor = (blankIndex: number): string => {
    try {
      return JSON.parse(fillInTheBlanksMaterialMap[String(blankIndex)])[0];
    } catch (error) {
      return "";
    }
  };
  // Every acceptable answer for a blank — grading must accept alternates, not
  // just the first entry (checkAnswerState handles arrays).
  const acceptedFor = (blankIndex: number): string[] => {
    try {
      return JSON.parse(fillInTheBlanksMaterialMap[String(blankIndex)]);
    } catch (error) {
      return [];
    }
  };

  const blankKeys = Object.keys(fillInTheBlanksMaterialMap || {});
  const hasValue = (key: string) => {
    const v = currentAnswerMap?.[key];
    return Boolean(v && v !== "DEFAULT_OPTION");
  };
  // V4: the active blank is the explicit target, else the first empty blank.
  const firstEmptyIndex = blankKeys.findIndex((key) => !hasValue(key));
  const effectiveTarget =
    fillTarget != null && !hasValue(String(fillTarget))
      ? fillTarget
      : firstEmptyIndex;
  const filledCount = blankKeys.filter((key) => hasValue(key)).length;

  // Words already sitting in a blank (struck through + disabled in the bank;
  // clearing the blank returns them).
  const placedValues = new Set(
    blankKeys
      .map((key) => currentAnswerMap?.[key])
      .filter((v) => v && v !== "DEFAULT_OPTION"),
  );

  // Word-bank tap → fill the active target, then auto-advance to the next
  // empty blank after it (V4 fillBlank).
  const fillWithWord = (word: string) => {
    if (!canAnswerQuestion() || effectiveTarget < 0) return;
    const key = String(effectiveTarget);
    const next = { ...currentAnswerMap, [key]: word };
    setCurrentAnswerMap(next);
    changeAnswer(next);
    const nextEmpty = blankKeys.findIndex(
      (k, i) =>
        i > effectiveTarget && !(next[k] && next[k] !== "DEFAULT_OPTION"),
    );
    setFillTarget(nextEmpty >= 0 ? nextEmpty : null);
  };
  const clearBlank = (blankIndex: number) => {
    if (!canAnswerQuestion()) return;
    handleFillInTheBlanksAnswerOnChange(answerMap, String(blankIndex), "");
    setFillTarget(blankIndex);
  };

  const renderFillBlank = (blankIndex: number) => {
    const key = String(blankIndex);
    const correct = correctFor(blankIndex);
    const value = showCorrectAnswer ? correct : currentAnswerMap?.[key] ?? "";
    const filled = Boolean(value && value !== "DEFAULT_OPTION");

    if (canAnswerQuestion() && !isPreview && !showCorrectAnswer) {
      const isTarget = effectiveTarget === blankIndex;
      return (
        <button
          type="button"
          className={`aas-fbslot ${filled ? "is-filled" : "is-empty"}${
            isTarget && !filled ? " is-target" : ""
          }`}
          onClick={() =>
            filled ? clearBlank(blankIndex) : setFillTarget(blankIndex)
          }
        >
          {filled ? (
            renderInlineParts(value)
          ) : (
            <span className="aas-fbph">
              {isTarget ? i18n.t("fib_pick_word") : i18n.t("fib_tap_to_fill")}
            </span>
          )}
          {filled ? (
            <span className="aas-fbx" aria-hidden="true">
              ×
            </span>
          ) : null}
        </button>
      );
    }

    // Graded (V4): correct → green slot + ✓; wrong → red slot + ✕; left EMPTY →
    // amber slot + "—". Wrong and empty both get the stacked "Doğru" chip below.
    // Empty used to be drawn as a wrong answer; every other template draws a
    // skipped item amber (they did not answer wrongly, they did not answer).
    const state = isPreview
      ? filled
        ? checkAnswerState(acceptedFor(blankIndex), value)
        : "EMPTY"
      : null;
    const isCorrect = showCorrectAnswer || state === "CORRECT";
    const isWrong = !showCorrectAnswer && state === "INCORRECT";
    const isBlank = !showCorrectAnswer && state === "EMPTY";

    const slot = (
      <span
        className={`aas-fbslot ${filled ? "is-filled" : "is-empty"}${
          isCorrect ? " is-correct" : ""
        }${isWrong ? " is-wrong" : ""}${isBlank ? " is-blank" : ""}`}
      >
        {filled ? renderInlineParts(value) : <span className="aas-fbph">—</span>}
        {isCorrect ? <span className="aas-fbic">✓</span> : null}
        {isWrong ? <span className="aas-fbic">✕</span> : null}
      </span>
    );

    if (isWrong || isBlank) {
      return (
        <span className="aas-astack">
          {slot}
          <span className="aas-corr">
            <span className="aas-corr-tag">{i18n.t("correct_label")}</span>
            <span className="aas-corr-val">{renderInlineParts(correct)}</span>
          </span>
        </span>
      );
    }
    return slot;
  };

  const renderPool = () => {
    if (!canAnswerQuestion() || isPreview || showCorrectAnswer) return null;
    return (
      <div className="aas-bank">
        <div className="aas-bank-head">
          <span className="aas-bank-label">{i18n.t("word_bank")}</span>
          <span className="aas-bank-count">
            {i18n.t("filled_count", {
              filled: filledCount,
              total: blankKeys.length,
            })}
          </span>
        </div>
        <div className="aas-bank-chips">
          {shuffledOptionList.map((option, index) => {
            const used = placedValues.has(option);
            return (
              <button
                key={`${option}-${index}`}
                type="button"
                disabled={used}
                className={`aas-word${used ? " is-used" : ""}`}
                onClick={() => !used && fillWithWord(option)}
              >
                {renderInlineParts(option)}
              </button>
            );
          })}
        </div>
        <p className="aas-bank-hint">{i18n.t("fill_in_the_blanks_hint")}</p>
      </div>
    );
  };

  return (
    <ActivitySolveLayout
      variant="inline"
      isFullScreen={isFullScreen}
      passage={passage}
      body={
        <>
          <p className="aas-inline-prompt">
            {i18n.t("please_select_fill_in_the_blanks_text")}
          </p>
          <InlineSentenceBody
            bodyMap={fillInTheBlanksBodyMap}
            renderBlank={renderFillBlank}
            totalBlanks={Object.keys(fillInTheBlanksMaterialMap || {}).length}
          />
        </>
      }
      interaction={renderPool()}
    />
  );
};

export default FillInTheBlanksActivityContent;
